import { FC, useCallback, useEffect, useState } from "react";
import { DateInputProps } from "./types";
import { useThemeStore } from "../../../stores";
import { Column, Row } from "../../containers";
import { AlignItems, JustifyContent, Wrap } from "../../containers/types";
import { Heading, Text } from "../../typography";
import {
  dateToTime,
  getMonthNrDays,
  getMonthStr,
  getYMD,
} from "../../../utils";
import { IconAnimation } from "../../animation";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "../../../assets/icons";
import { UpdateFn } from "../../../types";
import { Button } from "../../buttons";

const DateInput: FC<DateInputProps> = ({
  date,
  setDate,
  label,
  props,
  className = "",
  style = {},
  background,
  color,
  readOnly = false,
}) => {
  const theme = useThemeStore((state) => state.theme);

  const color_ =
    background === theme.background ? theme.textOnBg : theme.textOnOverlay;

  const [day, setDay] = useState<string>("");
  const [month, setMonth] = useState<string>("");
  const [year, setYear] = useState<string>("");

  const [selectorMonth, setSelectorMonth] = useState<number>(0);
  const [selectorYear, setSelectorYear] = useState<number>(0);

  useEffect(() => {
    const { day, month, year } = getYMD(date);
    setDay(day);
    setMonth(month);
    setSelectorMonth(Number(month));
    setYear(year);
    setSelectorYear(Number(year));
  }, [date]);

  const [open, setOpen] = useState<boolean>(false);

  const onNextMonth = useCallback(() => {
    if (selectorMonth < 12) setSelectorMonth((prev) => prev + 1);
    else {
      setSelectorMonth(1);
      setSelectorYear((prev) => prev + 1);
    }
  }, [selectorMonth]);

  const onPrevMonth = useCallback(() => {
    if (selectorMonth > 1) setSelectorMonth((prev) => prev - 1);
    else {
      setSelectorMonth(12);
      setSelectorYear((prev) => prev - 1);
    }
  }, [selectorMonth]);

  const selectDate = useCallback(
    (d: number) => {
      setDate(
        dateToTime(
          d.toString(),
          selectorMonth.toString(),
          selectorYear.toString()
        )
      );
      setOpen(false);
    },
    [selectorMonth, selectorYear, setDate]
  );

  const onInpChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    set: UpdateFn<number>,
    from: number,
    to: number
  ) => {
    if (e.target.value.includes(".")) return;
    let val = Number(e.target.value);
    if (isNaN(val)) return;
    if (val < from || val > to) return;
    set(Number(e.target.value.trim()));
  };

  const goToToday = (): void => {
    setDate(Date.now());
  };

  return (
    <Column
      alignItems={AlignItems.Start}
      justifyContent={JustifyContent.Start}
      gap={5}
      className={className}
      style={{ width: "100%", position: "relative", ...style }}
      props={props}
    >
      <Heading color={color || theme.textOnOverlay} size={16}>
        {label}
      </Heading>
      <Row
        style={{
          borderRadius: !open ? 15 : "15px 15px 0 0",
          background: background || theme.background,
          width: "calc(100% - 10px)",
          height: 41,
          paddingRight: 10,
        }}
        justifyContent={JustifyContent.Between}
      >
        <Row>
          <input
            style={{ color: color_, ...styles.inp }}
            value={day}
            readOnly
          />
          <Text color={color_}>/</Text>
          <input
            style={{ color: color_, ...styles.inp }}
            value={month}
            readOnly
          />
          <Text color={color_}>/</Text>
          <input
            style={{ color: color_, ...styles.inp }}
            value={year}
            readOnly
          />
        </Row>
        <IconAnimation
          Icon={CalendarIcon}
          onHoverColor={theme.primary}
          width={20}
          height={20}
          style={{ cursor: "pointer", marginLeft: "1rem" }}
          onClick={() => {
            if (readOnly) return;
            setOpen((prev) => !prev);
          }}
        />
      </Row>
      {open ? (
        <Column
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "calc(100% - 10px)",
            minHeight: 200,
            boxShadow: `0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)`,
            borderRadius: "0 0 15px 15px",
            zIndex: 9,
            padding: 5,
          }}
          background={background || theme.background}
          justifyContent={JustifyContent.Between}
          gap={20}
        >
          <Row
            justifyContent={JustifyContent.Between}
            style={{ width: "100%" }}
          >
            <IconAnimation
              Icon={ChevronRightIcon}
              onHoverColor={theme.secondary}
              style={{ cursor: "pointer" }}
              width={18}
              height={18}
              onClick={onNextMonth}
            />
            <Text color={color_} size={16}>
              {getMonthStr(selectorMonth)}
            </Text>
            <IconAnimation
              Icon={ChevronLeftIcon}
              onHoverColor={theme.secondary}
              style={{ cursor: "pointer" }}
              width={18}
              height={18}
              onClick={onPrevMonth}
            />
          </Row>
          <Row
            reverse
            flexWrap={Wrap.Wrap}
            style={{ width: "100%" }}
            gap={10}
            justifyContent={JustifyContent.Center}
          >
            {[...Array(getMonthNrDays(selectorYear, selectorMonth))].map(
              (_, idx) => (
                <Row
                  key={idx}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 7,
                    cursor: "pointer",
                  }}
                  justifyContent={JustifyContent.Center}
                  background={
                    idx + 1 === Number(day) &&
                    selectorMonth === Number(month) &&
                    selectorYear === Number(year)
                      ? theme.primary
                      : "transparent"
                  }
                  props={{ onClick: () => selectDate(idx + 1) }}
                >
                  <Text
                    size={18}
                    color={
                      idx + 1 === Number(day) &&
                      selectorMonth === Number(month) &&
                      selectorYear === Number(year)
                        ? theme.textOnprimary
                        : color_
                    }
                  >
                    {String(idx + 1)}
                  </Text>
                </Row>
              )
            )}
          </Row>
          <Row
            style={{ width: "100%" }}
            justifyContent={JustifyContent.Between}
          >
            <Row>
              <input
                style={{ color: color_, ...styles.inp }}
                value={selectorMonth}
                onChange={(e) => onInpChange(e, setSelectorMonth, 0, 12)}
              />
              <Text color={color_}>/</Text>
              <input
                style={{ color: color_, ...styles.inp }}
                value={selectorYear}
                onChange={(e) => onInpChange(e, setSelectorYear, 0, 5000)}
              />
            </Row>
            <Button
              background={theme.secondary}
              color={theme.textonSecondary}
              style={{ width: 100, height: 30 }}
              onClick={goToToday}
            >
              برو به امروز
            </Button>
          </Row>
        </Column>
      ) : (
        <></>
      )}
    </Column>
  );
};

export default DateInput;

const styles: Record<string, React.CSSProperties> = {
  inp: {
    maxWidth: 50,
    background: "transparent",
    textAlign: "center",
  },
};
